/**
 * DataLoader: loads tree and metadata from URLs or embedded values.
 */

export type CsvRow = Record<string, string>;
export type ColumnType = 'number' | 'text';

/** Input configuration for loadData(). */
export interface LoadDataConfig {
  treeUrl?: string;
  tree?: string;
  metadataUrl?: string;
  metadata?: CsvRow[];
}

/** Output shape returned by loadData(). */
export interface LoadedData {
  tree?: string;
  metadata?: CsvRow[];
}

interface DataLoaderModule {
  loadData(config: LoadDataConfig): Promise<LoadedData>;
  fetchText(url: string): Promise<string>;
  fetchCSV(url: string): Promise<CsvRow[]>;
  parseCSV(text: string): CsvRow[];
  detectColumnTypes(rows: CsvRow[]): Record<string, ColumnType>;
  getUniqueValues(rows: CsvRow[], columnName: string): string[];
}

export const DataLoader: DataLoaderModule = {
  async loadData(config) {
    try {
      const data: LoadedData = {};

      if (config.treeUrl) {
        data.tree = await this.fetchText(config.treeUrl);
      } else if (config.tree) {
        data.tree = config.tree;
      }

      if (config.metadataUrl) {
        data.metadata = await this.fetchCSV(config.metadataUrl);
      } else if (config.metadata) {
        data.metadata = config.metadata;
      }

      return data;
    } catch (error) {
      console.error('Failed to load data:', error);
      throw error;
    }
  },

  async fetchText(url) {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.text();
  },

  async fetchCSV(url) {
    const text = await this.fetchText(url);
    return this.parseCSV(text);
  },

  parseCSV(text) {
    // Handle \r\n (Windows), \n (Unix), \r (old Mac).
    const lines = text.trim().split(/\r?\n|\r/);
    if (lines.length === 0) return [];

    // noUncheckedIndexedAccess: length check above proves lines[0] exists.
    const headerLine = lines[0]!;
    const headers = headerLine.split(',').map((h) => h.trim());

    const rows: CsvRow[] = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (!line || !line.trim()) continue;

      const values = line.split(',').map((v) => v.trim());
      const row: CsvRow = {};
      headers.forEach((header, index) => {
        row[header] = values[index] ?? '';
      });
      rows.push(row);
    }

    return rows;
  },

  detectColumnTypes(rows) {
    if (!rows || rows.length === 0) return {};

    const types: Record<string, ColumnType> = {};
    const firstRow = rows[0];
    if (!firstRow) return {};

    const headers = Object.keys(firstRow);

    headers.forEach((header) => {
      const values = rows.map((r) => r[header]).filter((v): v is string => v !== '' && v !== undefined);

      if (values.length === 0) {
        types[header] = 'text';
        return;
      }

      const allNumbers = values.every((v) => !isNaN(parseFloat(v)));
      types[header] = allNumbers ? 'number' : 'text';
    });

    return types;
  },

  getUniqueValues(rows, columnName) {
    const values = new Set<string>();
    rows.forEach((row) => {
      const value = row[columnName];
      if (value !== null && value !== undefined && value !== '') {
        values.add(value);
      }
    });
    return Array.from(values).sort();
  },
};
